/**
 * Context selector component.
 *
 * Displays available contexts and allows selection.
 *
 * @module ContextSelector
 */

import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Card, CardContent } from '@/components/ui/card';
import { Plus, XCircle, Eye, FileImage, FileAudio, Loader2 } from 'lucide-react';
import { contextTypeConfig } from './constants';
import { type Context } from '@/lib/contextApi';

interface ContextSelectorProps {
  contexts: Context[];
  selectedContextId: number | null;
  loadingContexts: boolean;
  showNewContextForm: boolean;
  onSelect: (contextId: number | null) => void;
  onClear: () => void;
  onCreateNew: () => void;
  onPreview: (contextId: number) => void;
  onNext: () => void;
  onCreateAndNext?: () => void;
  uploading?: boolean;
  canCreateAndNext?: boolean;
}

export const ContextSelector = ({
  contexts,
  selectedContextId,
  loadingContexts,
  showNewContextForm,
  onSelect,
  onClear,
  onCreateNew,
  onPreview,
  onNext,
  onCreateAndNext,
  uploading = false,
  canCreateAndNext = false,
}: ContextSelectorProps) => {
  const sortedContexts = [...contexts].sort((a, b) => {
    if (selectedContextId === a.id) return -1;
    if (selectedContextId === b.id) return 1;
    return 0;
  });

  return (
    <div>
      <Label className="font-ui">Context Selection</Label>
      <p className="font-ui text-sm text-muted-foreground mb-4">
        Select or create a context for your questions
      </p>

      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <h4 className="font-ui font-medium">Existing Contexts</h4>
          <div className="flex gap-2">
            {selectedContextId !== null && (
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={onClear}
                className="text-muted-foreground hover:text-foreground"
              >
                <XCircle className="h-4 w-4 mr-1" />
                <span className="font-ui">Clear</span>
              </Button>
            )}
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={onCreateNew}
            >
              <Plus className="h-4 w-4 mr-2" />
              <span className="font-ui">Create New</span>
            </Button>
          </div>
        </div>
      </div>

      {!showNewContextForm && (
        <>
          {loadingContexts && (
            <div className="font-ui text-center py-4">
              <Loader2 className="h-4 w-4 animate-spin inline mr-2" />
              Loading...
            </div>
          )}
          {!loadingContexts && contexts.length === 0 && (
            <div className="font-ui text-center py-4 text-muted-foreground">
              No contexts available
            </div>
          )}
          {!loadingContexts && contexts.length > 0 && (
            <div className="grid gap-3 max-h-96 overflow-y-auto mt-6">
              {sortedContexts.map((context) => (
                  <Card
                    key={context.id}
                    className={`cursor-pointer transition-all ${
                      selectedContextId === context.id
                        ? 'ring-2 ring-primary bg-primary/5 border-primary'
                        : 'hover:shadow-md'
                    }`}
                    onClick={() => {
                      onSelect(
                        selectedContextId === context.id ? null : context.id,
                      );
                    }}
                  >
                    <CardContent className="p-4">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-2">
                            <h5
                              className={`font-ui font-medium ${
                                selectedContextId === context.id
                                  ? 'text-primary'
                                  : ''
                              }`}
                            >
                              {context.name}
                            </h5>
                            {context.type !== 'text' &&
                              context.hasFile &&
                              (context.type === 'image' ? (
                                <FileImage
                                  className={`h-4 w-4 ${
                                    selectedContextId === context.id
                                      ? 'text-primary'
                                      : 'text-muted-foreground'
                                  }`}
                                />
                              ) : (
                                <FileAudio
                                  className={`h-4 w-4 ${
                                    selectedContextId === context.id
                                      ? 'text-primary'
                                      : 'text-muted-foreground'
                                  }`}
                                />
                              ))}
                          </div>
                          {context.type === 'text' ? (
                            <p className="font-ui text-sm text-muted-foreground mt-1 line-clamp-1">
                              {context.textPreview ||
                                context.content?.substring(0, 100) +
                                  (context.content?.length > 100 ? '...' : '')}
                            </p>
                          ) : (
                            <div className="flex items-center gap-2 mt-1">
                              <span className="font-ui text-sm text-muted-foreground">
                                {`${context.type} file` + (context.fileExtension ? ` (.${context.fileExtension})` : '')}
                              </span>
                              <span
                                className={`font-ui text-xs px-2 py-1 rounded ${
                                  contextTypeConfig[context.type]?.badgeColor ||
                                  'bg-muted text-muted-foreground'
                                }`}
                              >
                                {context.type}
                              </span>
                              {selectedContextId === context.id && (
                                <span className="font-ui text-xs bg-primary text-primary-foreground px-2 py-1 rounded font-medium">
                                  Selected
                                </span>
                              )}
                            </div>
                          )}
                        </div>
                        <Button
                          type="button"
                          variant={
                            selectedContextId === context.id
                              ? 'default'
                              : 'ghost'
                          }
                          size="icon"
                          onClick={(e) => {
                            e.stopPropagation();
                            onPreview(context.id);
                          }}
                        >
                          <Eye className="h-4 w-4" />
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                ))}
            </div>
          )}
        </>
      )}

      <div className="flex justify-between pt-4 border-t">
        <div></div>
        <div className="flex gap-2">
          {showNewContextForm && canCreateAndNext && onCreateAndNext ? (
            <Button
              type="button"
              onClick={onCreateAndNext}
              disabled={uploading}
            >
              {uploading ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                  <span className="font-ui">Creating...</span>
                </>
              ) : (
                <span className="font-ui">Create Context and Next</span>
              )}
            </Button>
          ) : (
            <Button type="button" onClick={onNext}>
              <span className="font-ui">Next</span>
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};
